import { mergeRollups } from "./adSpend.js";
import { REGEX_EMAIL } from "./regex.js";

// Inlined so node:test can load this file (constants.js pulls the app
// bundle). Must match ADVERTISER_ROLE in utils/constants.js.
const ADVERTISER_ROLE = "advertiser";

const EMAIL_MAX_LENGTH = 254;
const LANGUAGE_MAX_LENGTH = 10;
const ADVERTISER_ID_MAX_LENGTH = 128;
const COUNTRY_PATTERN = /^[A-Z]{2}$/;

const SIGNUP_FIELDS = new Set([
  "uid",
  "email",
  "country",
  "agencyID",
  "advertiserID",
  "accountEmail",
  "language",
  "source",
]);

export const ADLE_CARD_WAITLIST_PATH = "adleCardWaitlist";
export const ADLE_CARD_WAITLIST_PROMPTS_PATH = "adleCardWaitlistPrompts";

export const ADLE_CARD_WAITLIST_SOURCE = {
  AUTO: "auto",
  BANNER: "banner",
};

const hasEntries = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

// Meta (facebook): businessAccounts. Google, TikTok Business, LinkedIn: advertisers.
const AD_ACCOUNT_COLLECTION_KEYS_BY_INTEGRATION = {
  facebook: ["businessAccounts"],
  google: ["advertisers"],
  tiktokBusiness: ["advertisers"],
  linkedIn: ["advertisers"],
};

const isConnectedAdAccount = ({ integration, integrationKey }) => {
  const collectionKeys =
    AD_ACCOUNT_COLLECTION_KEYS_BY_INTEGRATION[integrationKey];
  if (!collectionKeys?.length || !hasEntries(integration)) return false;
  return Object.values(integration).some((record) =>
    collectionKeys.some((collectionKey) => hasEntries(record?.[collectionKey]))
  );
};

const hasConnectedAdAccountOnPlatform = ({
  integrations,
  adAccountIntegrationKeys,
}) =>
  adAccountIntegrationKeys.some((key) =>
    isConnectedAdAccount({
      integration: integrations?.[key],
      integrationKey: key,
    })
  );

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizeAdleCardWaitlistEmail = (email) =>
  String(email ?? "")
    .trim()
    .toLowerCase();

export const getIsAdleCardWaitlistEligible = ({
  user,
  payments,
  integrations,
  adAccountIntegrationKeys = [],
  isPrivilegedLogin,
  isAdleDomain,
  isWhiteLabelBrand,
  isAdminHost,
} = {}) => {
  if (!user?.uid || user?.isAnonymous) return false;
  if (user?.role !== ADVERTISER_ROLE) return false;
  // The signup rules require both auth claims; without them a submit can
  // never succeed, so the prompt is not shown.
  if (typeof user?.agencyID !== "string" || !user.agencyID) return false;
  if (typeof user?.advertiserID !== "string" || !user.advertiserID) {
    return false;
  }
  if (payments?.isPlanActiveResolved !== true) return false;
  if (payments?.isPlanActive !== true) return false;
  if (isPrivilegedLogin === true) return false;
  if (user?.supportSessionExpiresAt != null) return false;
  if (isAdleDomain !== true || isWhiteLabelBrand || isAdminHost) return false;

  return hasConnectedAdAccountOnPlatform({
    integrations,
    adAccountIntegrationKeys,
  });
};

export const getShouldAutoOpenAdleCardWaitlist = ({
  isEligible,
  isStatusResolved,
  hasJoined,
  hasDismissed,
  hasAutoOpenedThisPageLoad,
} = {}) =>
  isEligible === true &&
  isStatusResolved === true &&
  hasJoined !== true &&
  hasDismissed !== true &&
  hasAutoOpenedThisPageLoad !== true;

/**
 * Rebrand banner slide. After "Not now", auto-open stays off (hasDismissed from
 * RTDB) but the banner remains a deliberate second entry point.
 */
export const getShouldShowAdleCardWaitlistBanner = ({
  isEligible,
  isStatusResolved,
  hasJoined,
} = {}) =>
  isEligible === true && isStatusResolved === true && hasJoined !== true;

export const buildAdleCardWaitlistSignup = ({
  uid,
  email,
  accountEmail,
  country,
  agencyID,
  advertiserID,
  language,
  source,
} = {}) => {
  const signup = {
    uid: typeof uid === "string" ? uid : "",
    email: normalizeAdleCardWaitlistEmail(email),
    country: String(country ?? "")
      .trim()
      .toUpperCase(),
  };

  // Auth claim only. Never substitute advertiserID when agencyID is missing.
  if (typeof agencyID === "string") {
    signup.agencyID = agencyID;
  }

  if (typeof advertiserID === "string") {
    signup.advertiserID = advertiserID;
  }

  if (
    typeof accountEmail === "string" &&
    accountEmail.length <= EMAIL_MAX_LENGTH
  ) {
    signup.accountEmail = accountEmail;
  }

  if (typeof language === "string" && language) {
    signup.language = language.slice(0, LANGUAGE_MAX_LENGTH);
  }

  if (
    source === ADLE_CARD_WAITLIST_SOURCE.AUTO ||
    source === ADLE_CARD_WAITLIST_SOURCE.BANNER
  ) {
    signup.source = source;
  }

  return signup;
};

export const isAdleCardWaitlistSignupWritable = (signup) => {
  if (!signup || typeof signup !== "object") return false;
  if (typeof signup.uid !== "string" || !signup.uid) return false;
  if (
    typeof signup.email !== "string" ||
    signup.email.length > EMAIL_MAX_LENGTH ||
    !REGEX_EMAIL.test(signup.email)
  ) {
    return false;
  }
  if (
    typeof signup.country !== "string" ||
    !COUNTRY_PATTERN.test(signup.country)
  ) {
    return false;
  }
  if (typeof signup.agencyID !== "string" || signup.agencyID.length === 0) {
    return false;
  }
  if (
    typeof signup.advertiserID !== "string" ||
    !signup.advertiserID ||
    signup.advertiserID.length > ADVERTISER_ID_MAX_LENGTH
  ) {
    return false;
  }
  if (Object.hasOwn(signup, "createdAt")) return false;

  const keys = Object.keys(signup);
  if (keys.some((key) => !SIGNUP_FIELDS.has(key))) return false;

  if (
    Object.hasOwn(signup, "accountEmail") &&
    (typeof signup.accountEmail !== "string" ||
      signup.accountEmail.length > EMAIL_MAX_LENGTH)
  ) {
    return false;
  }

  if (
    Object.hasOwn(signup, "language") &&
    (typeof signup.language !== "string" ||
      !signup.language ||
      signup.language.length > LANGUAGE_MAX_LENGTH)
  ) {
    return false;
  }

  if (
    Object.hasOwn(signup, "source") &&
    signup.source !== ADLE_CARD_WAITLIST_SOURCE.AUTO &&
    signup.source !== ADLE_CARD_WAITLIST_SOURCE.BANNER
  ) {
    return false;
  }

  return true;
};

const listSignups = (signups) => {
  if (Array.isArray(signups)) return signups.filter(Boolean);
  if (!signups || typeof signups !== "object") return [];
  return Object.entries(signups).map(([uid, value]) => ({
    uid,
    ...(value && typeof value === "object" ? value : {}),
  }));
};

export const collectAgencyIDsFromWaitlistSignups = (signups) => {
  const ids = new Set();
  listSignups(signups).forEach((signup) => {
    const agencyID = signup?.agencyID;
    if (typeof agencyID === "string" && agencyID) ids.add(agencyID);
  });
  return [...ids];
};

export const collectAdvertiserIDsFromWaitlistSignups = (signups) => {
  const ids = new Set();
  listSignups(signups).forEach((signup) => {
    const advertiserID = signup?.advertiserID;
    if (typeof advertiserID === "string" && advertiserID) ids.add(advertiserID);
  });
  return [...ids];
};

const resolveWaitlistBrandName = ({
  advertiserID,
  advertisersMeta,
  advertiserNamesById = {},
}) => {
  const fromAdvertiser = advertiserNamesById[advertiserID];
  if (typeof fromAdvertiser === "string" && fromAdvertiser.trim()) {
    return fromAdvertiser.trim();
  }
  const fromSpendMeta = advertisersMeta?.[advertiserID]?.businessName;
  if (typeof fromSpendMeta === "string" && fromSpendMeta.trim()) {
    return fromSpendMeta.trim();
  }
  return advertiserID || "";
};

const indexAdvertiserIDsByAgency = (advertisersMeta) => {
  const byAgency = new Map();
  Object.entries(advertisersMeta || {}).forEach(([advertiserID, meta]) => {
    const agencyID = meta?.agencyID;
    if (!agencyID) return;
    if (!byAgency.has(agencyID)) byAgency.set(agencyID, new Set());
    byAgency.get(agencyID).add(advertiserID);
  });
  return byAgency;
};

const advertiserIDsForAgency = ({ agencyID, advertiserIDsByAgency }) => {
  const ids = new Set(advertiserIDsByAgency.get(agencyID));
  // The principal brand uses advertiserID === agencyID and may be missing
  // from advertisersMeta.
  if (agencyID) ids.add(String(agencyID));
  return ids;
};

// Newest first, matching orderByChild("creationTimeNegative") (ascending).
// A missing negative falls back to -creationTime. Rows with neither sort last.
const creationTimeSortValue = (row) => {
  const negative = row?.creationTimeNegative;
  if (negative != null && Number.isFinite(Number(negative))) {
    return Number(negative);
  }
  const creationTime = row?.creationTime;
  if (creationTime != null && Number.isFinite(Number(creationTime))) {
    return -Number(creationTime);
  }
  return 0;
};

const sumSpend = ({ rollup, advertiserIDs }) => {
  if (rollup == null) return null;
  let sum = 0;
  advertiserIDs.forEach((advertiserID) => {
    sum += toFiniteNumber(rollup?.byAdvertiser?.[advertiserID]?.spendUSD);
  });
  return sum;
};

export const buildAdleCardWaitlistRows = ({
  signups,
  allTime,
  monthlyByKey,
  advertisersMeta,
  agencyNamesById = {},
  advertiserNamesById = {},
} = {}) => {
  const advertiserIDsByAgency = indexAdvertiserIDsByAgency(advertisersMeta);
  const last3Months =
    monthlyByKey == null
      ? null
      : mergeRollups({ rollups: Object.values(monthlyByKey) });

  const rows = listSignups(signups).map((signup) => {
    const agencyID = signup?.agencyID || "";
    const advertiserIDs = advertiserIDsForAgency({
      agencyID,
      advertiserIDsByAgency,
    });
    const advertiserID = signup?.advertiserID || "";
    const brandName = resolveWaitlistBrandName({
      advertiserID,
      advertisersMeta,
      advertiserNamesById,
    });
    const agencyRecordName = agencyNamesById[agencyID];
    const agencyName =
      (typeof agencyRecordName === "string" && agencyRecordName.trim()) ||
      brandName ||
      "";

    return {
      uid: signup?.uid || "",
      email: signup?.email || "",
      country: signup?.country || "",
      agencyID,
      agencyName,
      advertiserID,
      brandName,
      creationTime: signup?.creationTime ?? null,
      creationTimeNegative: signup?.creationTimeNegative ?? null,
      accountEmail: signup?.accountEmail || "",
      language: signup?.language || "",
      source: signup?.source || "",
      adSpendAllTimeUSD: sumSpend({ rollup: allTime, advertiserIDs }),
      adSpendLast3MonthsUSD: sumSpend({ rollup: last3Months, advertiserIDs }),
    };
  });

  return rows.toSorted(
    (left, right) => creationTimeSortValue(left) - creationTimeSortValue(right)
  );
};
